/**
 * Client HTTP UNIQUE de la console (audit console §1 « Couche API »).
 *
 * Bibliothèque : axios (instance + intercepteur). Aucun mécanisme maison :
 * - `createApiClient` fabrique une instance axios dont l'intercepteur pose
 *   le jeton Firebase (`Authorization: Bearer`) et l'environnement de
 *   l'onglet (`X-Environment`) — une seule copie de ce code pour toute
 *   la console ;
 * - `bindEnvironmentSource` : l'`EnvironmentProvider` y branche SA valeur
 *   (source unique, propre à l'onglet) ; l'intercepteur ne lit jamais le
 *   localStorage partagé entre onglets ;
 * - `withEnv(env)` force l'environnement d'UN appel (mutations argent,
 *   requêtes dont la clé de cache porte l'environnement) ;
 * - `ApiError` / `toApiError` : modèle d'erreur commun (statut, code,
 *   message affichable) pour les retours visibles à l'écran.
 *
 * Transport UNIQUE de l'environnement : l'en-tête `X-Environment`
 * (functions/src/utils/request_env.py `explicit_env`, accepté aussi par
 * `@require_env`). Exceptions, imposées par le backend et commentées sur
 * place : le corps JSON des routes qui lisent `body_env(…, défaut)`
 * (migration, signalements) et les
 * paramètres de synchronisation source / cible (env-sync).
 */

import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../config/adminApi';
import { AppEnvironment, DEFAULT_ENVIRONMENT } from '../config/environment';

/** En-tête de sélection d'environnement attendu par le backend. */
export const ENV_HEADER = 'X-Environment';

/** Délais réseau nommés (ms). */
export const HTTP_TIMEOUT = {
  /** Envois de fond qui ne doivent jamais bloquer l'écran (suivi d'activité). */
  SHORT: 10_000,
  /** Lecture ou écriture courante. */
  DEFAULT: 30_000,
  /** Agrégats, exports, imports de taille moyenne. */
  LONG: 60_000,
  /** Traitements lourds (migration, génération IA). */
  EXTENDED: 120_000,
  /** Traitements par lots. */
  BATCH: 180_000,
} as const;

export interface ApiClientOptions {
  /** URL de base (défaut : fonction `api`, `API_URL`). */
  baseURL?: string;
  /** Délai maximal de la requête (défaut : `HTTP_TIMEOUT.DEFAULT`). */
  timeout?: number;
}

/**
 * Jeton Firebase de l'utilisateur connecté, ou `null` sans session.
 * Une erreur de Firebase remonte à l'appelant (jamais de requête partie
 * silencieusement sans jeton à cause d'un incident).
 */
export const getAuthToken = async (): Promise<string | null> => {
  const user = getAuth().currentUser;
  return user ? user.getIdToken() : null;
};

/** Lecteur de l'environnement courant de l'onglet. */
export type EnvironmentSource = () => AppEnvironment;

/** Repli avant le montage du fournisseur (aucune requête n'est censée partir). */
const unboundSource: EnvironmentSource = () => DEFAULT_ENVIRONMENT;

let environmentSource: EnvironmentSource = unboundSource;

/**
 * Branche la source de l'environnement de l'onglet (appelé par
 * `EnvironmentProvider`). Renvoie la fonction de débranchement.
 */
export const bindEnvironmentSource = (source: EnvironmentSource): (() => void) => {
  environmentSource = source;
  return () => {
    if (environmentSource === source) {
      environmentSource = unboundSource;
    }
  };
};

/** Environnement courant de l'onglet (lecture hors React : suivi d'activité). */
export const currentEnvironment = (): AppEnvironment => environmentSource();

/** Intercepteur commun : jeton + environnement (sauf environnement forcé). */
export const attachAuthAndEnvironment = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.headers[ENV_HEADER]) {
    config.headers[ENV_HEADER] = currentEnvironment();
  }
  return config;
};

/** Instance axios authentifiée qui porte l'environnement sélectionné. */
export const createApiClient = (options: ApiClientOptions = {}): AxiosInstance => {
  const client = axios.create({
    baseURL: options.baseURL ?? API_URL,
    timeout: options.timeout ?? HTTP_TIMEOUT.DEFAULT,
  });
  client.interceptors.request.use(attachAuthAndEnvironment);
  return client;
};

/** Configuration axios qui force l'environnement d'un appel. */
export const withEnv = (env: AppEnvironment, config: AxiosRequestConfig = {}): AxiosRequestConfig => ({
  ...config,
  headers: { ...(config.headers ?? {}), [ENV_HEADER]: env },
});

type QueryValue = string | number | boolean | null | undefined;

/**
 * Paramètres de requête sans les filtres vides : un filtre facultatif non
 * renseigné (`undefined`, `''`) n'est pas envoyé.
 */
export const presentParams = (params: Record<string, QueryValue>): Record<string, string | number | boolean> =>
  Object.keys(params).reduce<Record<string, string | number | boolean>>((kept, key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      kept[key] = value;
    }
    return kept;
  }, {});

/** Erreur d'appel API normalisée (affichable telle quelle). */
export class ApiError extends Error {
  readonly status: number | null;
  readonly code: string | null;

  constructor(message: string, status: number | null, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

interface ErrorBody {
  message?: unknown;
  error?: unknown;
  code?: unknown;
}

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/**
 * Normalise n'importe quelle erreur levée par un appel API.
 * Message : `message` du corps (forme commune du backend), sinon `error`
 * (forme historique), sinon le message de l'erreur, sinon `fallback`.
 */
export const toApiError = (error: unknown, fallback: string): ApiError => {
  if (error instanceof ApiError) {
    return error;
  }
  if (axios.isAxiosError(error)) {
    const body = (error.response?.data ?? {}) as ErrorBody;
    const message = asText(body.message) ?? asText(body.error) ?? fallback;
    return new ApiError(message, error.response?.status ?? null, asText(body.code));
  }
  const message = error instanceof Error ? asText(error.message) : null;
  return new ApiError(message ?? fallback, null, null);
};

/** Message affichable d'une erreur d'appel API. */
export const toUserMessage = (error: unknown, fallback: string): string =>
  toApiError(error, fallback).message;

/**
 * Propriété texte d'une erreur levée (`message`, ou `code` des erreurs
 * Firebase), ou `undefined` : remplace l'accès direct sur une erreur non typée.
 */
export const errorField = (error: unknown, field: 'message' | 'code'): string | undefined => {
  const value = typeof error === 'object' && error !== null ? (error as Record<string, unknown>)[field] : undefined;
  return typeof value === 'string' ? value : undefined;
};

/** Champ texte (`error` ou `message`) du corps d'une réponse d'erreur HTTP. */
export const responseField = (error: unknown, field: 'error' | 'message'): string | undefined => {
  const body = axios.isAxiosError(error) ? (error.response?.data as ErrorBody | undefined) : undefined;
  const value = body?.[field];
  return typeof value === 'string' ? value : undefined;
};
